import { AsyncThunk, AnyAction } from "@reduxjs/toolkit";
import { isPlainObject } from "redux";
import { isObjectEmpty } from "./objectUtil";

type GenericAsyncThunk = AsyncThunk<unknown, unknown, any>;

type PendingAction = ReturnType<GenericAsyncThunk["pending"]>;
type RejectedAction = ReturnType<GenericAsyncThunk["rejected"]>;
type FulfilledAction = ReturnType<GenericAsyncThunk["fulfilled"]>;

export function isPendingAction(key: string) {
  return (action: AnyAction): action is PendingAction =>
    new RegExp(`${key}/.+/pending`).test(action.type);
}

export function isRejectedAction(key: string) {
  return (action: AnyAction): action is RejectedAction =>
    new RegExp(`${key}/.+/rejected`).test(action.type);
}

export function isFulfilledAction(key: string) {
  return (action: AnyAction): action is FulfilledAction =>
    new RegExp(`${key}/.+/fulfilled`).test(action.type);
}

const isEmptyValue = (value: any) => {
  return (
    value === null ||
    value === undefined ||
    (typeof value === "number" && isNaN(value)) ||
    (Array.isArray(value) && value.length === 0) ||
    value === "" ||
    (typeof value === "object" && isObjectEmpty(value))
  );
};

export const deepMerge = (state: any, payload: any) => {
  const nextState = { ...state };

  for (const key in payload) {
    if (Object.prototype.hasOwnProperty.call(payload, key)) {
      const payloadValue = payload[key];
      const stateValue = nextState?.[key];

      if (payloadValue !== undefined && !isEmptyValue(payloadValue)) {
        if (Array.isArray(payloadValue) && !payloadValue.length) {
          nextState[key] = stateValue;
        } else {
          nextState[key] = payloadValue; // Replace with new value if it's not empty
        }
      }
      if (stateValue === undefined) {
        nextState[key] = payloadValue; // Additional fields from the payload
      }
    }
  }
  return nextState;
};

export const deepMergeRecursive = (state: any, payload: any) => {
  const nextState = { ...state };

  for (const key in payload) {
    if (Object.prototype.hasOwnProperty.call(payload, key)) {
      const payloadValue = payload[key];
      const stateValue = nextState?.[key];

      if (payloadValue !== undefined && !isEmptyValue(payloadValue)) {
        if (Array.isArray(payloadValue) && !payloadValue.length) {
          nextState[key] = stateValue;
        } else if (isPlainObject(payloadValue)) {
          nextState[key] = deepMergeRecursive(stateValue, payloadValue); // Recursive call for nested objects
        } else {
          nextState[key] = payloadValue; // Replace with new value if it's not empty
        }
      }
      if (stateValue === undefined) {
        nextState[key] = payloadValue; // Additional fields from the payload
      }
    }
  }
  return nextState;
};

export const redirectToAccessDeniedProps = () => {
  return {
    redirect: {
      destination: "/errors/access_denied",
      permanent: false,
    },
  };
};
